/**
 * A canvas panel that floats in front of the user with the world-space keyboard folded out
 * beneath it when a text field is active. The query form and the query card build on it.
 */
import * as THREE from 'three';
import { CanvasPanel, type Region } from '../dashboards/CanvasPanel.ts';
import { Keyboard } from '../ui/CanvasKeyboard.ts';
import type { InteractiveSystem } from '../ui/Interactive.ts';
import { TextField } from './textField.ts';

export interface FloatingPanelSounds {
  key(): void;
  click(): void;
  /** Called synchronously when a text field takes or releases the keyboard. */
  typing?(on: boolean): void;
}

export abstract class FloatingPanel {
  readonly group = new THREE.Group();
  readonly panel: CanvasPanel;
  protected readonly keyboard: Keyboard;
  readonly field = new TextField();
  /** Whether the keyboard is out and key events go to the field. */
  typing = false;
  private attached = false;

  constructor(
    protected readonly interactive: InteractiveSystem,
    size: { width: number; height: number; pixelWidth: number; pixelHeight: number; name: string },
    protected readonly sounds?: FloatingPanelSounds,
  ) {
    this.panel = new CanvasPanel(size);
    this.panel.mesh.renderOrder = 60;
    this.group.add(this.panel.mesh);
    this.keyboard = new Keyboard(interactive, {
      onInput: (ch) => this.onInput(ch),
      onBackspace: () => this.onBackspace(),
      onEnter: () => this.onEnter(),
      onKeySound: () => sounds?.key(),
    });
    this.keyboard.group.position.set(0, -size.height / 2 - 0.2, 0.14);
    this.keyboard.group.rotation.x = -0.45;
    this.keyboard.setVisible(false);
    this.group.add(this.keyboard.group);
    this.group.visible = false;
    this.group.name = size.name;
    this.panel.setDraw((ctx, panel) => this.draw(ctx, panel));
    this.panel.onSelect = (region) => this.select(region);
  }

  get isOpen(): boolean {
    return this.group.visible;
  }

  /** Places the panel between `origin` and the viewer, upright, facing the viewer. */
  protected place(origin: THREE.Vector3, viewer: THREE.Vector3, distance = 0.45): void {
    const dir = new THREE.Vector3(viewer.x - origin.x, 0, viewer.z - origin.z);
    if (dir.lengthSq() < 1e-6) dir.set(0, 0, 1);
    dir.normalize();
    this.group.position.copy(origin).addScaledVector(dir, distance);
    this.group.position.y = THREE.MathUtils.clamp(origin.y, 1.15, 1.65);
    this.group.rotation.set(0, Math.atan2(dir.x, dir.z), 0);
  }

  protected show(): void {
    this.group.visible = true;
    if (!this.attached) {
      this.panel.attach(this.interactive);
      this.attached = true;
    }
    this.panel.invalidate();
  }

  hide(): void {
    if (!this.group.visible) return;
    this.group.visible = false;
    this.setTyping(false);
    if (this.attached) {
      this.panel.detach(this.interactive);
      this.attached = false;
    }
  }

  setTyping(on: boolean): void {
    this.typing = on;
    this.keyboard.setVisible(on && this.group.visible);
    // Capture must flip before the next key event, not on the next frame.
    this.sounds?.typing?.(on);
    this.panel.invalidate();
  }

  /** Physical keyboard on desktop; returns true when consumed. */
  handleKeyEvent(e: KeyboardEvent): boolean {
    if (!this.isOpen) return false;
    if (e.key === 'Escape') {
      this.onEscape();
      return true;
    }
    if (!this.typing) return false;
    return this.keyboard.handleKeyEvent(e);
  }

  update(): void {
    if (!this.isOpen) return;
    this.keyboard.update();
    this.panel.update();
  }

  dispose(): void {
    this.hide();
    this.panel.dispose();
  }

  protected onInput(ch: string): void {
    this.field.input(ch);
    this.panel.invalidate();
  }

  protected onBackspace(): void {
    this.field.backspace();
    this.panel.invalidate();
  }

  protected abstract onEnter(): void;
  protected abstract onEscape(): void;
  protected abstract draw(ctx: CanvasRenderingContext2D, panel: CanvasPanel): void;
  protected abstract select(region: Region): void;
}
